import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';

// ---------- Types ----------
export interface LubricantProduct {
  id: number;
  name: string;
  makerId: number;
  categoryId: number;
  categoryName: string;
  typeId: number | null;
  typeName: string | null;
  productGroupId: number | null;
  productGroupName: string | null;
  uomId: number;
  symbol: string;
  active: boolean;
  typeIn: boolean;
  typeInManagerId: number | null;
  updatedBy: number;
  updatedOn: string;
}

export interface LubricantProductsResponse {
  success: boolean;
  message: string;
  data: any | LubricantProduct[];
  timestamp: string;
  errors: any;
  requestId: string;
}

export interface GetLubricantProductsParams {
  search?: string;
  active?: boolean;
  makerId?: number;
}

interface CreateLubricantProductPayload {
  name: string;
  makerId: number | null;
  categoryId: number | null;
  typeId: number | null;
  productGroupId: number | null;
  typeInManagerId: number | null;
  uomId: number | null;
  status: boolean;
  typeIn: boolean | null;
}

interface UpdateLubricantProductPayload {
  productName: string;
  makerId: number;
  categoryid: number;
  typeId: number;
  productGroupId: number;
  uomId: number;
  status: boolean;
  // updatedBy: number;
}

export interface GetLubricantProductsListParams {
  [key: string]: any;
}

export interface UnitOfMeasurement {
  id: number;
  symbol: string;
}

export interface UnitsOfMeasurementResponse {
  data: UnitOfMeasurement[];
}

// ---------- API Slice ----------
const lubricantProductsApi = createApi({
  reducerPath: 'lubricantProductsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['LubricantProduct', 'TypeInProducts'],
  endpoints: (builder) => ({
    getLubricantProducts: builder.query<
      LubricantProductsResponse,
      GetLubricantProductsParams | void
    >({
      query: (params) => {
        const queryParams = params || {};
        return {
          url: '/lubricant',
          method: 'GET',
          params: {
            ...params,
            ...(queryParams.search && { search: queryParams.search }),
          },
        };
      },
      providesTags: ['LubricantProduct'],
    }),

    createLubricantProduct: builder.mutation<any, CreateLubricantProductPayload>({
      query: (payload) => ({
        url: '/lubricant',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['LubricantProduct', 'TypeInProducts'],
    }),

    updateLubricantProduct: builder.mutation<
      any,
      { id: number; payload: UpdateLubricantProductPayload }
    >({
      query: ({ id, payload }) => ({
        url: `/lubricant/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LubricantProduct' as const, id: id.toString() },
        'LubricantProduct',
      ],
    }),

    deleteLubricantProduct: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/lubricant/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LubricantProduct'],
    }),

    getUnitsOfMeasurement: builder.query<UnitsOfMeasurementResponse, void>({
      query: () => ({
        url: '/units-of-measurement',
        method: 'GET',
      }),
      transformResponse: (res: UnitOfMeasurement[]): UnitsOfMeasurementResponse => ({
        data: res,
      }),
    }),
  }),
});

// ---------- Exports ----------
export const lubricantProductsReducer = lubricantProductsApi.reducer;
export const lubricantProductsMiddleware = lubricantProductsApi.middleware;
export const lubricantProductsReducerPath = lubricantProductsApi.reducerPath;

export const {
  useGetLubricantProductsQuery,
  useCreateLubricantProductMutation,
  useUpdateLubricantProductMutation,
  useDeleteLubricantProductMutation,
  useGetUnitsOfMeasurementQuery,
} = lubricantProductsApi;
